import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2, KeyRound, Save, Shield, User } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

function CreateUserDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "sales" });
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/users", form);
      toast.success("Utilizator creat");
      setForm({ email: "", password: "", name: "", role: "sales" });
      onCreated();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="create-user-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Utilizator nou</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Nume</Label><Input data-testid="cu-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ex: Andrei Popescu" /></div>
          <div><Label>Email</Label><Input data-testid="cu-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
          <div><Label>Parolă inițială (min 6 caractere)</Label><Input data-testid="cu-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} /></div>
          <div>
            <Label>Rol</Label>
            <div className="flex gap-2 mt-1">
              {[["sales", "Limited (sales)"], ["admin", "Admin"]].map(([v, label]) => (
                <button key={v} type="button" data-testid={`cu-role-${v}`}
                  onClick={() => setForm({ ...form, role: v })}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${
                    form.role === v ? "bg-[#0A0F1C] text-white border-[#0A0F1C]" : "bg-white border-slate-200 text-slate-600"
                  }`}
                >{label}</button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Anulează</Button>
            <Button data-testid="cu-submit" type="submit" disabled={loading} className="bg-[#0047FF] hover:bg-[#003BD3]">
              {loading ? "Se creează..." : "Creează"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ open, onClose, target }) {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/users/${target.id}`, { password: pwd });
      toast.success(`Parolă schimbată pentru ${target.email}`);
      setPwd("");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Resetează parola</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-slate-500 mb-1">Pentru: <strong className="text-slate-900">{target?.email}</strong></div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Parolă nouă (min 6 caractere)</Label>
            <Input data-testid="rp-input" type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} required minLength={6} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Anulează</Button>
            <Button data-testid="rp-submit" type="submit" disabled={loading} className="bg-[#0047FF] hover:bg-[#003BD3]">
              {loading ? "Se salvează..." : "Salvează"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);

  async function load() {
    try {
      const { data } = await api.get("/users");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }
  useEffect(() => { load(); }, []);

  async function changeRole(u, role) {
    if (u.role === role) return;
    try {
      await api.put(`/users/${u.id}`, { role });
      toast.success(`Rol schimbat: ${u.email} → ${role}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function remove(u) {
    if (u.id === me.id) { toast.error("Nu îți poți șterge propriul cont"); return; }
    if (!window.confirm(`Ștergi utilizatorul ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Șters");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <div className="sm:pl-52 space-y-4" data-testid="users-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Echipă</div>
          <h1 className="font-display text-3xl tracking-tight font-medium">Utilizatori</h1>
        </div>
        <button data-testid="add-user-btn" onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 bg-[#0047FF] hover:bg-[#003BD3] text-white text-sm px-3 py-2 rounded-lg">
          <UserPlus className="w-4 h-4" /> Adaugă utilizator
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
        {items.map((u) => {
          const isMe = u.id === me?.id;
          const RoleIcon = u.role === "admin" ? Shield : User;
          return (
            <div key={u.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`user-row-${u.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${u.role === "admin" ? "bg-[#0A0F1C] text-white" : "bg-slate-100 text-slate-600"}`}>
                  <RoleIcon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{u.name || u.email.split("@")[0]} {isMe && <span className="text-[10px] uppercase tracking-widest text-slate-400 ml-1">(tu)</span>}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <select
                  data-testid={`role-select-${u.id}`}
                  value={u.role}
                  onChange={(e) => changeRole(u, e.target.value)}
                  disabled={isMe}
                  className="text-xs bg-white border border-slate-200 rounded-full px-2.5 py-1 disabled:opacity-50"
                >
                  <option value="admin">Admin</option>
                  <option value="sales">Sales</option>
                </select>
                <button data-testid={`reset-pwd-${u.id}`} onClick={() => setResetTarget(u)} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-[#0047FF]" title="Resetează parola">
                  <KeyRound className="w-4 h-4" />
                </button>
                <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u)} disabled={isMe} className="p-2 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30" title="Șterge">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <div className="p-10 text-center text-slate-400 text-sm">Niciun utilizator.</div>}
      </div>

      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />
      {resetTarget && <ResetPasswordDialog open={!!resetTarget} onClose={() => setResetTarget(null)} target={resetTarget} />}
    </div>
  );
}
